/**
 * Fill rate — SKU × facility × city.
 *
 * Fill rate is delivered ÷ ordered on the LINES, never an average of per-line
 * rates: a 1,000-unit line short by half matters more than a 2-unit line
 * delivered whole. Only CLOSED, uncensored lines count, in both numerator and
 * denominator, so a purchase order still in flight never reads as a failure —
 * and the window stops at the settled frontier, because the lines that close
 * first are the ones that were filled and a half-closed day reads several
 * points high.
 *
 * The MRP/SP toggle is hidden on this page: fill rate is a unit ratio with no
 * rupee in it.
 */
import type { ReactNode } from 'react';
import { RowLabel, TableCard, fmt } from '../ds';
import { ErrorBox, State } from '../Panels';
import { useReport } from '../useReport';
import {
  type DrillRow, type OpsWindow,
  DrillBody, GoodZero, bandColor, num, periodLabel, str, sumOf,
} from './opsLib';
import type { DateRange } from '@/lib/dateRange';

interface Lane {
  key: string;
  sku: string;
  productId: string;
  facility: string;
  city: string;
  fill: number | null;
  lines: number | null;
  ordered: number | null;
  delivered: number | null;
  short: number | null;
}

function lanesOf(rows: Array<Record<string, unknown>>): Lane[] {
  return rows.map((r) => ({
    // product_id is in the key because two facilities can stock the same
    // item name and the pair is what makes a lane unique.
    key: `${str(r['product_id'])}|${str(r['facility'])}|${str(r['city'])}`,
    sku: str(r['sku']) || '—',
    productId: str(r['product_id']),
    facility: str(r['facility']) || '—',
    city: str(r['city']) || '—',
    fill: num(r['fill_rate']),
    lines: num(r['closed_lines']),
    // The API names these `closed_*` — they are the CLOSED atoms, and asking
    // for `ordered`/`delivered`/`lines` is what once left four of the eight
    // columns blank on every row.
    ordered: num(r['closed_units_ordered']),
    delivered: num(r['closed_delivered_quantity']),
    short: num(r['closed_short']),
  })).filter((l) => (l.ordered ?? 0) > 0)
    // Ranked by the units actually missed, which is what the page is for.
    .sort((a, b) => (b.short ?? 0) - (a.short ?? 0));
}

/** Port of skuSub: the product id under the name, so a lane can be looked up
 *  in the portal without leaving the row. */
function SkuLabel({ sku, productId, extra }: { sku: string; productId: string; extra?: string }) {
  const parts = [
    productId ? <>product_id <span className="rowlabel-link">{productId}</span></> : null,
    extra ?? null,
  ].filter(Boolean);
  return (
    <RowLabel
      name={sku}
      sub={parts.length > 0 ? parts.map((p, i) => <span key={i}>{i > 0 ? ' · ' : ''}{p}</span>) : undefined}
    />
  );
}

const COLS = [
  { label: 'SKU' },
  { label: 'Facility' },
  { label: 'City' },
  {
    label: 'Fill rate',
    type: 'num' as const,
    title: 'CLOSED units delivered / CLOSED units ordered, for lines ORDERED inside the window whose date has settled (open and censored lines excluded)',
  },
  { label: 'Lines', type: 'num' as const, title: 'PO lines that have CLOSED, of the lines ordered in the window' },
  { label: 'Ordered', type: 'num' as const },
  { label: 'Delivered', type: 'num' as const },
  { label: 'Short', type: 'num' as const },
];

const laneCells = (l: Lane): ReactNode[] => [
  <SkuLabel key="s" sku={l.sku} productId={l.productId} />,
  <span key="f" style={{ color: 'var(--ink-2)' }}>{l.facility}</span>,
  <span key="c" style={{ color: 'var(--ink-2)' }}>{l.city}</span>,
  <span key="r" style={{ color: bandColor(l.fill), fontWeight: 600 }}>{fmt(l.fill, 'pct')}</span>,
  <span key="n" style={{ color: 'var(--ink-3)' }}>{fmt(l.lines, 'int')}</span>,
  fmt(l.ordered, 'int'),
  fmt(l.delivered, 'int'),
  // A shortfall is red; a zero shortfall is grey, so a good zero never reads as
  // a failure. A MISSING shortfall is an em-dash and is not coloured at all.
  l.short === null ? '—' : (
    <GoodZero key="sh" value={l.short}>
      <span style={{ color: 'var(--down)' }}>{fmt(l.short, 'int')}</span>
    </GoodZero>
  ),
];

export function FillRateCard({ customer, range }: { customer: string; range: DateRange }) {
  const q = useReport('/v1/reports/ops/fill-rate', customer, range);
  // The headline rate and the settled frontier are computed by the database
  // over the WHOLE scope and served on the control tower. Summing this table
  // instead would report the fill rate of its 200-row cap as the brand's.
  const head = useReport('/v1/reports/ops/control-tower', customer, range);

  if (q.isLoading) return <State>Loading purchase orders…</State>;
  if (q.isError) return <ErrorBox>{q.error.message}</ErrorBox>;

  const w: OpsWindow | undefined = q.data?.window;
  const lanes = lanesOf(q.data?.rows ?? []);
  const basis = head.data?.fill_rate_basis;

  /*
   * Fill rate follows the picker, on the CLOSE date.
   *
   * Purchase orders close 7-9 days after the order date, so last_7_days - the
   * default on every page - is entirely in flight and has nothing to report.
   * The server retreats to the last window of the same length that HAS
   * settled; this labels that retreat instead of printing the retreated
   * figures under the selected dates, which is the one thing that would be
   * worse than the empty page this replaces.
   */
  const scope = periodLabel(w?.start, w?.end);

  /*
   * The empty state the client asked for, in their words: "if no po is closed
   * in the selected period then it should say no po closed in the selected
   * period". On the close-date basis that is the only empty case there is —
   * there is no longer a "not settled yet", because every PO in range has
   * closed by definition.
   */
  if (lanes.length === 0) {
    return (
      <section className="card">
        <div className="card-head"><span className="card-title">Fill rate</span></div>
        <div className="empty">No purchase order closed in {scope}.</div>
      </section>
    );
  }

  return (
    <TableCard
      title="Fill rate — SKU × facility × city"
      scope={`closed ${scope}`}
      asof={basis?.last_close ? `Closes to ${basis.last_close}` : `Data to ${w?.as_of ?? '—'}`}
      cols={COLS}
      rows={lanes.map((l): DrillRow => ({ key: l.key, cells: laneCells(l) }))}
      foot={(
        <>
          <b>These are the purchase orders that CLOSED in {scope}</b>, which is what the
          period means here: delivered ÷ ordered across those POs, summed on the units and never
          averaged across lines &mdash; a 1,000-unit line short by half matters more than a 2-unit
          line delivered whole. An order raised in this window but not yet closed is not counted
          and is not a miss; it will appear in whichever period it closes in. Censored lines, still
          open when they aged out of the partner export, have an unknown outcome and are excluded
          from both sides.{' '}
          <b>This does not match the published report</b>, which windows on the ORDER date and
          waits for each day to settle. The two answer different questions &mdash; "how much of
          what I ordered arrived" against "how complete was what settled" &mdash; and will not
          agree.{' '}
          {head.data?.fill_rate !== null && head.data?.fill_rate !== undefined ? (
            <>
              Headline for your selection:{' '}
              <b>{fmt(head.data.fill_rate, 'pct')}</b>
              {head.data.fill_rate_previous !== null && head.data.fill_rate_previous !== undefined ? (
                <>
                  {' '}against <b>{fmt(head.data.fill_rate_previous, 'pct')}</b> over{' '}
                  {periodLabel(basis?.previous_window_start, basis?.previous_window_end)}
                </>
              ) : null}{' '}over{' '}
              {fmt(basis?.closed_lines, 'int')} purchase orders closed in this period
              {basis?.last_close ? `, to ${basis.last_close}` : ''}.{' '}
            </>
          ) : (
            <>
              No purchase order closed in this period, so there is no rate to quote.{' '}
            </>
          )}
          There is deliberately <b>no daily fill-rate column</b>: purchase orders close in lumps, so
          a day with one PO reads 100% or 0%. The PO-by-PO history behind a lane is not served by
          this API yet, which is why these rows do not open.
        </>
      )}
    />
  );
}

/** One city's purchase-order lanes — opened from a city row. */
export function CityPoDrill({ customer, range, city }: {
  customer: string;
  range: DateRange;
  city: string;
}) {
  const q = useReport('/v1/reports/ops/fill-rate', customer, range, { city });
  const w: OpsWindow | undefined = q.data?.window;
  const lanes = lanesOf(q.data?.rows ?? []);

  // Pooled, not averaged: the totals row divides summed delivered by summed
  // ordered, which is the same derivation the database uses for the headline.
  const ordered = sumOf(lanes, (l) => l.ordered) ?? 0;
  const delivered = sumOf(lanes, (l) => l.delivered) ?? 0;
  const pooled = ordered > 0 ? (delivered / ordered) * 100 : null;

  return (
    <DrillBody
      title={`Purchase orders — ${city}`}
      count={`${lanes.length} lane${lanes.length === 1 ? '' : 's'} · ordered ${periodLabel(w?.start, w?.end)}`}
      loading={q.isLoading}
      error={q.isError ? q.error.message : null}
      empty={lanes.length === 0 ? `No PO lines ordered into ${city} in ${periodLabel(w?.start, w?.end)} have closed yet.` : null}
      cols={COLS.filter((c) => c.label !== 'City')}
      rows={[
        ...lanes.map((l): DrillRow => ({
          key: l.key,
          cells: laneCells(l).filter((_, i) => i !== 2),
        })),
        ...(lanes.length > 0 ? [{
          key: '__total',
          cls: 'is-total',
          cells: [
            <b key="t">These {lanes.length} lane{lanes.length === 1 ? '' : 's'}</b>,
            '',
            <b key="r" style={{ color: bandColor(pooled) }}>{fmt(pooled, 'pct')}</b>,
            <b key="n">{fmt(sumOf(lanes, (l) => l.lines), 'int')}</b>,
            <b key="o">{fmt(ordered, 'int')}</b>,
            <b key="d">{fmt(delivered, 'int')}</b>,
            <b key="s">{fmt(ordered - delivered, 'int')}</b>,
          ] as ReactNode[],
        }] : []),
      ]}
      foot={(
        <>
          <span>
            Lanes whose purchase orders were <b>ordered</b> into {city} inside the selected window
            and have since closed. The totals row pools &mdash; summed delivered over summed ordered
            &mdash; rather than averaging the lane percentages, so a 1,000-unit lane outweighs a
            2-unit one. Sorted by units short.
          </span>
          <span className="spacer" />
          <span>Data to {w?.as_of ?? '—'}</span>
        </>
      )}
    />
  );
}
